import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, Upload, Trash2, UploadCloud } from 'lucide-react';

interface Card {
  name: string;
  url: string;
}

interface Deck {
  id: number;
  name: string;
  membership: string;
  deckType: string;
  deckUrl?: string;
  isActive: boolean;
}

interface CardEditorProps {
  readonly deckId: number;
}

export function CardEditor({ deckId }: Readonly<CardEditorProps>) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cardName, setCardName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const multipleFileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  
  // Get the deck information
  const { data: deck, isLoading: isDeckLoading } = useQuery({
    queryKey: [`/api/manager/decks/${deckId}`],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/manager/decks/${deckId}`);
      if (!response.ok) throw new Error('Failed to fetch deck');
      return response.json() as Promise<Deck>;
    },
  });
  
  
  // Get the card list
  const { 
    data: cards, 
    isLoading: isCardsLoading,
    error: cardsError
  } = useQuery({
    queryKey: [`/api/manager/decks/${deckId}/cards`],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/manager/decks/${deckId}/cards`);
      if (!response.ok) throw new Error('Failed to fetch cards');
      return response.json() as Promise<Card[]>;
    },
    enabled: !!deck, // Only run when deck data is available
  });
  
  // Upload a card file
  const uploadCardMutation = useMutation({
    mutationFn: async ({ file, cardName }: { file: File, cardName: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('cardName', cardName);
      
      const response = await apiRequest('POST', `/api/manager/decks/${deckId}/cards`, formData);
      if (!response.ok) throw new Error('Failed to upload card');
      return response.json() as Promise<Card>;
    },
    onSuccess: () => {
      toast({
        title: "Card uploaded",
        description: `${cardName} has been uploaded successfully.`,
      });
      setSelectedFile(null);
      setCardName("");
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      // Refresh the card list
      queryClient.invalidateQueries({ queryKey: [`/api/manager/decks/${deckId}/cards`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload card file.",
        variant: "destructive",
      });
    },
  });
  
  // Delete a card file
  const deleteCardMutation = useMutation({
    mutationFn: async (cardName: string) => {
      const response = await apiRequest('DELETE', `/api/manager/decks/${deckId}/cards/${cardName}`);
      if (!response.ok) throw new Error('Failed to delete card');
      return response.json();
    },
    onSuccess: (_, cardName) => {
      toast({
        title: "Card deleted",
        description: `${cardName} has been deleted successfully.`,
      });
      // Refresh the card list
      queryClient.invalidateQueries({ queryKey: [`/api/manager/decks/${deckId}/cards`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Delete failed",
        description: error.message || "Failed to delete card file.",
        variant: "destructive",
      });
    },
  });
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null);
  };

  const handleMultipleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    
    setIsUploading(true);
    setUploadProgress({ current: 0, total: e.target.files.length });
    
    let successCount = 0;
    let failedCount = 0;
    
    for (let i = 0; i < e.target.files.length; i++) {
      const file = e.target.files[i];
      const fileName = file.name.split('.')[0];
      
      if (RegExp(/^[A-F]\d{1,2}$/).exec(fileName)) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('cardName', `${fileName}.svg`);
          
          await apiRequest('POST', `/api/manager/decks/${deckId}/cards`, formData);
          
          successCount++;
        } catch (error) {
          if (error instanceof Error) {
            console.error(`Failed to upload ${file.name}:`, error);
          }
          failedCount++;
        }
      } else {
        failedCount++;
      }
      
      setUploadProgress(prev => ({ ...prev, current: i + 1 }));
    }
    
    // Refresh the card list
    queryClient.invalidateQueries({ queryKey: [`/api/manager/decks/${deckId}/cards`] });
    
    // Clear the file input
    if (multipleFileInputRef.current) {
      multipleFileInputRef.current.value = "";
    }
    
    // Show success/failure toast
    if (successCount > 0) {
      toast({
        title: "Cards uploaded",
        description: `Successfully uploaded ${successCount} cards.`,
      });
    }
    
    if (failedCount > 0) {
      toast({
        title: "Some uploads failed",
        description: `${failedCount} cards failed to upload. Make sure file names follow the format A2, B10, etc.`,
        variant: "destructive",
      });
    }
    
    setIsUploading(false);
  };
  
  const handleCardNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardName(e.target.value);
  };
  
  const handleUpload = () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload.",
        variant: "destructive",
      });
      return;
    }
    
    // Special case for card back
    if (cardName === 'back' || cardName === 'back.svg') {
      const finalCardName = cardName.endsWith('.svg') ? cardName : `${cardName}.svg`;
      uploadCardMutation.mutate({ file: selectedFile, cardName: finalCardName });
      return;
    }

    // For regular cards, validate the format
    const cardPattern = /^[A-F]\d{1,2}$/;
    const cardPatternWithExt = /^[A-F]\d{1,2}\.svg$/;
    
    if (!cardPattern.exec(cardName) && !cardPatternWithExt.exec(cardName)) {
      toast({
        title: "Invalid card name",
        description: "Card name should follow the format A2, B10, etc. or be 'back' for card back",
        variant: "destructive",
      });
      return;
    }
    
    // Add .svg extension if not already present
    const finalCardName = cardName.endsWith('.svg') ? cardName : `${cardName}.svg`;
    uploadCardMutation.mutate({ file: selectedFile, cardName: finalCardName });
  };
  
  const handleDeleteCard = (cardName: string) => {
    if (confirm(`Are you sure you want to delete ${cardName}?`)) {
      deleteCardMutation.mutate(cardName);
    }
  };
  
  if (isDeckLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }
  
  if (!deck) {
    return (
      <div className="p-4 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Deck not found</h3>
        <p className="text-muted-foreground">The selected deck could not be found.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h3 className="text-lg font-bold">{deck.name}</h3>
          <p className="text-sm text-muted-foreground">
            {deck.membership} / {deck.deckType}
          </p>
        </div>
        
      </div>

      <Separator />
      
      {/* New batch upload section */}
      <div>
        <h4 className="text-md font-medium mb-4">Batch Upload Cards</h4>
        <div className="bg-muted/30 rounded-lg p-4 border border-muted">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="multipleFiles">Select Multiple Card Files</Label>
              <p className="text-xs text-muted-foreground">
                Files must be named in the format A2.svg, B10.svg, etc. File names will be used as card names.
              </p>
              <Input
                id="multipleFiles"
                type="file"
                onChange={handleMultipleFileChange}
                accept=".svg"
                multiple
                disabled={isUploading}
                ref={multipleFileInputRef}
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <Button 
                onClick={() => multipleFileInputRef.current?.click()}
                disabled={isUploading}
                className="w-full"
                variant="outline"
              >
                {isUploading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Uploading {uploadProgress.current}/{uploadProgress.total}...
                  </>
                ) : (
                  <>
                    <UploadCloud className="h-4 w-4 mr-2" />
                    Select and Upload Multiple Cards
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <Separator />

      {/* New dedicated section for card back */}
      <div>
        <h4 className="text-md font-medium mb-4">Upload Card Back</h4>
        <div className="bg-primary/10 rounded-lg p-4 border border-primary/30">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm mb-4">
                The card back is a required file that will be displayed for opponent's cards during gameplay.
                Upload it as 'back.svg'.
              </p>
              {cards?.find(c => c.name === 'back.svg') ? (
                <div className="flex items-center gap-4">
                  <div className="w-24 h-32 relative">
                    <img 
                      src={cards.find(c => c.name === 'back.svg')?.url}
                      alt="Card Back"
                      className="w-full h-full object-contain border rounded"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground mb-2">Current card back</p>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteCard('back.svg')}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Replace Card Back
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-destructive/10 text-destructive px-4 py-2 rounded">
                  No card back uploaded yet. This is required for gameplay.
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="backFile">Card Back File (SVG)</Label>
                <Input
                  id="backFile"
                  type="file"
                  onChange={(e) => {
                    setSelectedFile(e.target.files?.[0] || null);
                    setCardName('back');
                  }}
                  accept=".svg"
                  className="mt-1"
                />
              </div>
              <Button 
                onClick={handleUpload}
                disabled={uploadCardMutation.isPending || !selectedFile || cardName !== 'back'}
                className="w-full"
              >
                {uploadCardMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Card Back
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Separator />
      
      <div>
        <h4 className="text-md font-medium mb-4">Upload Single Card</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="cardName">Card Name</Label>
            <Input
              id="cardName"
              value={cardName}
              onChange={handleCardNameChange}
              placeholder="e.g., A2 or B10 or back"
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Format: A2 to A14, B2 to B14, etc., or "back" for card back image (.svg will be added automatically)
            </p>
          </div>
          <div>
            <Label htmlFor="cardFile">Card File (SVG)</Label>
            <Input
              id="cardFile"
              type="file"
              onChange={handleFileChange}
              accept=".svg"
              className="mt-1"
              ref={fileInputRef}
            />
          </div>
          <div className="flex items-end">
            <Button 
              onClick={handleUpload}
              disabled={uploadCardMutation.isPending || !selectedFile || !cardName}
              className="w-full"
            >
              {uploadCardMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Card
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      
      <Separator />
      
      <div>
        <h4 className="text-md font-medium mb-4">Card Files</h4>
        
        {isCardsLoading ? (
          <div className="flex justify-center py-4">
            <RefreshCw className="animate-spin h-6 w-6" />
          </div>
        ) : cardsError ? (
          <div className="p-4 bg-destructive/10 rounded-lg text-center">
            <p className="text-muted-foreground">
              {cardsError.message || "Failed to load card files."}
            </p>
            <Button
              variant="outline"
              className="mt-2"
              onClick={() => queryClient.invalidateQueries({ queryKey: [`/api/manager/decks/${deckId}/cards`] })}
            >
              Retry
            </Button>
          </div>
        ) : !cards || cards.length === 0 ? (
          <div className="p-4 bg-muted rounded-lg text-center">
            <p className="text-muted-foreground">
              No card files found. Upload your first card file.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {cards.map((card) => (
              <div key={card.name} className="relative group">
                <div className="aspect-[3/4] bg-muted rounded-lg overflow-hidden border flex items-center justify-center">
                  <img 
                    src={card.url} 
                    alt={card.name}
                    className="max-h-full max-w-full object-contain p-2"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMjJDMTcuNTIyOCAyMiAyMiAxNy41MjI4IDIyIDEyQzIyIDYuNDc3MTUgMTcuNTIyOCAyIDEyIDJDNi40NzcxNSAyIDIgNi40NzcxNSAyIDEyQzIgMTcuNTIyOCA2LjQ3NzE1IDIyIDEyIDIyWiIgc3Ryb2tlPSJjdXJyZW50Q29sb3IiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48L3BhdGg+PHBhdGggZD0iTTkgMTVIMTVNOSA5SDE1TTEyIDYuNVYxNy41IiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjwvcGF0aD48L3N2Zz4=";
                    }}
                  />
                </div>
                <div className="mt-1 text-xs font-medium text-center">{card.name}</div>
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleDeleteCard(card.name)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}